import React, { useState } from 'react';
import './Login.css';
import { API_BASE_URL } from '../../utils/api';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    const trimmedPassword = password;

    if (!trimmedEmail || !trimmedPassword) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);
    setShowLoader(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, password: trimmedPassword })
      });
      console.log('Login response:', response);
      const data = await response.json();

      if (data.success) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('username', data.username || trimmedEmail);
        localStorage.setItem('email', data.email || trimmedEmail);
        localStorage.setItem('role', data.role || 'member');
        if (data.avatarUrl) {
          localStorage.setItem('avatarUrl', data.avatarUrl);
        }
        onLogin();
      } else {
        setError(data.error || 'Invalid credentials');
      }
    } catch (err) {
      if (err.message && err.message.includes('fetch')) {
        setError('Failed to connect to server. Please ensure the backend is running on port 5000.');
      } else {
        setError(err.message || 'Failed to connect to server');
      }
      console.error('Login error:', err);
    } finally {
      setLoading(false);
      setShowLoader(false);
    }
  };

  const handleGoogleLogin = async () => {
    setShowLoader(true);
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    // Debug: Log the client ID (remove in production)
    console.log('Google Client ID from env:', clientId ? 'Found' : 'Not found', clientId ? `(${clientId.substring(0, 20)}...)` : '');
    
    // Check if client ID is missing, empty, or placeholder
    if (!clientId || clientId.trim() === '' || clientId === 'YOUR_GOOGLE_CLIENT_ID' || clientId.includes('your-google-client-id')) {
      setError('Google login is not configured. Please add your Google Client ID to the .env file and restart the server. See GOOGLE_LOGIN_SETUP.md for instructions.');
      console.error('Google Client ID not found. Current value:', clientId);
      return;
    }

    try {
      // Load Google Identity Services
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        
        script.onload = () => {
          initializeGoogleSignIn(clientId);
        };
        
        script.onerror = () => {
          setError('Failed to load Google login service');
        };
      } else {
        initializeGoogleSignIn(clientId);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setError('Failed to initialize Google login. Please try again.');
      setShowLoader(false);
    }
  };

  const initializeGoogleSignIn = (clientId) => {
    try {
      if (!window.google || !window.google.accounts) {
        setError('Google login service not available');
        setShowLoader(false);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCallback,
      });

      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Fallback to popup
          try {
            const tokenClient = window.google.accounts.oauth2.initTokenClient({
              client_id: clientId,
              scope: 'email profile',
              callback: handleGoogleTokenResponse,
            });
            tokenClient.requestAccessToken();
          } catch (err) {
            console.error('Token client error:', err);
            setError('Failed to start Google login. Please try again.');
            setShowLoader(false);
          }
        }
      });
    } catch (error) {
      console.error('Google sign-in initialization error:', error);
      setError('Failed to initialize Google login');
      setShowLoader(false);
    }
  };

  const handleGoogleCallback = async (response) => {
    try {
      setLoading(true);
      setError('');
      
      // Send Google's token WHOLE - header, payload and signature.
      //
      // This used to do atob(response.credential.split('.')[1]), which reads
      // the middle section and throws the signature away, then posted the
      // email it found. Anything could be typed into that request, so the
      // server had no way to tell a real sign-in from a made-up one. The
      // signature is the only part that proves Google issued it, and only the
      // server can check it, so the server gets the lot.
      const apiResponse = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential: response.credential }),
      });

      const data = await apiResponse.json();

      if (data.success) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('username', data.username);
        localStorage.setItem('email', data.email);
        localStorage.setItem('role', data.role || 'member');
        if (data.avatarUrl) {
          localStorage.setItem('avatarUrl', data.avatarUrl);
        }
        onLogin();
      } else {
        setError(data.error || 'Google login failed');
      }
    } catch (error) {
      console.error('Google callback error:', error);
      setError('Failed to process Google login');
    } finally {
      setLoading(false);
      setShowLoader(false);
    }
  };

  const handleGoogleTokenResponse = async (tokenResponse) => {
    try {
      setLoading(true);
      setError('');
      
      // The popup path has no signed ID token, only an access token - so the
      // profile is no longer read here and posted on. The access token goes to
      // our server, which asks Google itself who it belongs to and checks it
      // was issued for this app. Same rule as above: the browser states a
      // claim, the server verifies it.
      const apiResponse = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken: tokenResponse.access_token }),
      });

      const data = await apiResponse.json();

      if (data.success) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('username', data.username);
        localStorage.setItem('email', data.email);
        localStorage.setItem('role', data.role || 'member');
        if (data.avatarUrl) {
          localStorage.setItem('avatarUrl', data.avatarUrl);
        }
        onLogin();
      } else {
        setError(data.error || 'Google login failed');
      }
    } catch (error) {
      console.error('Google token response error:', error);
      setError('Failed to process Google login');
    } finally {
      setLoading(false);
      setShowLoader(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-backdrop" />
      {showLoader && (
        <div className="tf-loader-overlay transparent">
          <div className="tf-loader">
            <div className="tf-badge">TF</div>
          </div>
        </div>
      )}
      <div className="login-modal">
        <div className="login-card">
          <div className="login-brand">
            <div className="logo">
              <span className="logo-text">TF</span>
            </div>
            <div className="brand-text">
              <h1>Welcome back</h1>
              <p>Sign in to continue to TestFlow</p>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-wrapper">
                <input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Toggle password visibility"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {showPassword ? (
                      <path d="M2.5 2.5L17.5 17.5M8.33333 8.33333C7.89131 8.77535 7.66667 9.39565 7.66667 10C7.66667 10.6043 7.89131 11.2247 8.33333 11.6667M8.33333 8.33333L11.6667 11.6667M8.33333 8.33333L5.83333 5.83333M11.6667 11.6667C12.1087 11.2247 12.3333 10.6043 12.3333 10C12.3333 9.39565 12.1087 8.77535 11.6667 8.33333M11.6667 11.6667L14.1667 14.1667M5.83333 5.83333C4.25 7.41667 2.5 9.58333 2.5 10C2.5 10.4167 4.25 12.5833 5.83333 14.1667M5.83333 5.83333L3.33333 3.33333M14.1667 14.1667C15.75 12.5833 17.5 10.4167 17.5 10C17.5 9.58333 15.75 7.41667 14.1667 5.83333M14.1667 14.1667L16.6667 16.6667" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <path d="M2.5 10C2.5 10 5.83333 5.83333 10 5.83333C14.1667 5.83333 17.5 10 17.5 10C17.5 10 14.1667 14.1667 10 14.1667C5.83333 14.1667 2.5 10 2.5 10Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="divider">
            <span>or</span>
          </div>

          <button
            type="button"
            className="social-button google-button"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.6 10.2273C19.6 9.51818 19.5364 8.83636 19.4182 8.18182H10V12.05H15.3818C15.15 13.3 14.4455 14.3591 13.3864 15.0682V17.5773H16.6182C18.5091 15.8364 19.6 13.2727 19.6 10.2273Z" fill="#4285F4"/>
              <path d="M10 20C12.7 20 14.9636 19.1045 16.6182 17.5773L13.3864 15.0682C12.4909 15.6682 11.3455 16.0227 10 16.0227C7.39545 16.0227 5.19091 14.2636 4.40455 11.9H1.06364V14.4909C2.70909 17.7591 6.09091 20 10 20Z" fill="#34A853"/>
              <path d="M4.40455 11.9C4.20455 11.3 4.09091 10.6591 4.09091 10C4.09091 9.34091 4.20455 8.7 4.40455 8.1V5.50909H1.06364C0.386364 6.85909 0 8.38636 0 10C0 11.6136 0.386364 13.1409 1.06364 14.4909L4.40455 11.9Z" fill="#FBBC05"/>
              <path d="M10 3.97727C11.4682 3.97727 12.7864 4.48182 13.8227 5.47273L16.6909 2.60455C14.9591 0.990909 12.6955 0 10 0C6.09091 0 2.70909 2.24091 1.06364 5.50909L4.40455 8.1C5.19091 5.73636 7.39545 3.97727 10 3.97727Z" fill="#EA4335"/>
            </svg>
            <span>Sign in with Google</span>
          </button>

        </div>
      </div>
    </div>
  );
}

export default Login;

